import React, { useState, useEffect, useReducer, useContext, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import IconButton from "@material-ui/core/IconButton";
import VisibilityIcon from "@material-ui/icons/Visibility";
import Chip from "@material-ui/core/Chip";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ChatiaDialog from "../../components/ChatiaDialog";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";

const reducer = (state, action) => {
  if (action.type === "LOAD_TRANSACTIONS") {
    const transactions = action.payload;
    const newTransactions = [];
    transactions.forEach((t) => {
      const idx = state.findIndex((s) => s.id === t.id);
      if (idx !== -1) {
        state[idx] = t;
      } else {
        newTransactions.push(t);
      }
    });
    return [...state, ...newTransactions];
  }
  if (action.type === "RESET") {
    return [];
  }
  return state;
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  filterContainer: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    flexWrap: "wrap",
  },
  formControl: {
    minWidth: 120,
  },
  chip: {
    fontWeight: "bold",
  },
  statsContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  statCard: {
    flex: 1,
    minWidth: 140,
    padding: theme.spacing(2),
    textAlign: "center",
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
  },
  statValue: {
    fontSize: "1.6rem",
    fontWeight: "bold",
  },
  statLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.8rem",
    marginTop: 2,
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(1, 0),
    borderBottom: "1px solid",
    borderColor: theme.palette.divider,
  },
  detailLabel: {
    fontWeight: "bold",
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(3),
  },
}));

const eventLabels = {
  purchase_approved: "Compra Aprovada",
  refund: "Reembolso",
  subscription_cancellation: "Cancelamento",
  subscription_expired: "Expirado",
  chargeback: "Chargeback",
  purchase_delayed: "Pagamento Pendente",
  unknown: "Desconhecido",
};

const eventColors = {
  purchase_approved: "#4caf50",
  refund: "#ffffff",
  subscription_cancellation: "#ff9800",
  subscription_expired: "#9e9e9e",
  chargeback: "#ffffff",
  purchase_delayed: "#ff9800",
};

const statusColors = {
  processed: "#4caf50",
  failed: "#ffffff",
  ignored: "#9e9e9e",
};

const Subscribers = () => {
  const classes = useStyles();
  const { user: loggedInUser } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [platform, setPlatform] = useState("");
  const [eventFilter, setEventFilter] = useState("");
  const [transactions, dispatch] = useReducer(reducer, []);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const stats = useMemo(() => {
    const approved = transactions.filter((t) => t.event === "purchase_approved").length;
    const refunds = transactions.filter((t) => t.event === "refund" || t.event === "chargeback").length;
    const cancellations = transactions.filter((t) => t.event === "subscription_cancellation").length;
    const totalAmount = transactions
      .filter((t) => t.event === "purchase_approved")
      .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);
    return { approved, refunds, cancellations, totalAmount };
  }, [transactions]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, platform, eventFilter]);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        const params = { searchParam, pageNumber };
        if (platform) params.platform = platform;
        if (eventFilter) params.event = eventFilter;
        const { data } = await api.get("/subscribers", { params });
        dispatch({ type: "LOAD_TRANSACTIONS", payload: data.transactions });
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    };
    fetchData();
  }, [searchParam, pageNumber, platform, eventFilter]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      setLoadingMore(true);
      setPageNumber((prev) => prev + 1);
    }
  };

  const handleViewDetail = async (transaction) => {
    try {
      const { data } = await api.get(`/subscribers/${transaction.id}`);
      setSelectedTransaction(data);
      setDetailOpen(true);
    } catch (err) {
      toastError(err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("pt-BR");
  };

  const formatCurrency = (amount, currency) => {
    if (!amount) return "-";
    const curr = currency || "BRL";
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: curr }).format(amount);
  };

  if (!loggedInUser?.super) {
    return <ForbiddenPage />;
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>Assinantes ({transactions.length})</Title>
        <MainHeaderButtonsWrapper>
          <div className={classes.filterContainer}>
            <FormControl className={classes.formControl} variant="outlined" size="small">
              <InputLabel>Plataforma</InputLabel>
              <Select
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                label="Plataforma"
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="braip">Braip</MenuItem>
                <MenuItem value="hotmart">Hotmart</MenuItem>
              </Select>
            </FormControl>
            <FormControl className={classes.formControl} variant="outlined" size="small">
              <InputLabel>Evento</InputLabel>
              <Select
                value={eventFilter}
                onChange={(e) => setEventFilter(e.target.value)}
                label="Evento"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="purchase_approved">Compra Aprovada</MenuItem>
                <MenuItem value="refund">Reembolso</MenuItem>
                <MenuItem value="subscription_cancellation">Cancelamento</MenuItem>
                <MenuItem value="chargeback">Chargeback</MenuItem>
                <MenuItem value="subscription_expired">Expirado</MenuItem>
              </Select>
            </FormControl>
            <TextField
              placeholder="Buscar por nome, email, afiliado..."
              type="search"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
          </div>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
        {/* Cards de Estatísticas */}
        <div className={classes.statsContainer}>
          <Paper className={classes.statCard} variant="outlined">
            <div className={classes.statValue} style={{ color: "#4caf50" }}>{stats.approved}</div>
            <div className={classes.statLabel}>Aprovadas</div>
          </Paper>
          <Paper className={classes.statCard} variant="outlined">
            <div className={classes.statValue} style={{ color: "#ffffff" }}>{stats.refunds}</div>
            <div className={classes.statLabel}>Reembolsos / Chargebacks</div>
          </Paper>
          <Paper className={classes.statCard} variant="outlined">
            <div className={classes.statValue} style={{ color: "#ff9800" }}>{stats.cancellations}</div>
            <div className={classes.statLabel}>Cancelamentos</div>
          </Paper>
          <Paper className={classes.statCard} variant="outlined">
            <div className={classes.statValue} style={{ color: "#2196f3" }}>
              {formatCurrency(stats.totalAmount)}
            </div>
            <div className={classes.statLabel}>Receita Aprovada</div>
          </Paper>
        </div>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">ID</TableCell>
              <TableCell align="center">Plataforma</TableCell>
              <TableCell align="center">Evento</TableCell>
              <TableCell align="center">Cliente</TableCell>
              <TableCell align="center">Email</TableCell>
              <TableCell align="center">Produto</TableCell>
              <TableCell align="center">Valor</TableCell>
              <TableCell align="center">Afiliado</TableCell>
              <TableCell align="center">Empresa</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Data</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((t) => (
              <TableRow key={t.id}>
                <TableCell align="center">{t.id}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={t.platform?.toUpperCase()}
                    size="small"
                    variant="outlined"
                    color={t.platform === "braip" ? "primary" : "secondary"}
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={eventLabels[t.event] || t.event}
                    size="small"
                    className={classes.chip}
                    style={{
                      backgroundColor: eventColors[t.event] || "#9e9e9e",
                      color: "#fff",
                    }}
                  />
                </TableCell>
                <TableCell align="center">{t.buyerName || "-"}</TableCell>
                <TableCell align="center">{t.buyerEmail || "-"}</TableCell>
                <TableCell align="center">{t.productName || "-"}</TableCell>
                <TableCell align="center">{formatCurrency(t.amount, t.currency)}</TableCell>
                <TableCell align="center">{t.affiliateName || t.affiliateCode || "-"}</TableCell>
                <TableCell align="center">
                  {t.company ? (
                    <Chip
                      label={`#${t.company.id} ${t.company.name}`}
                      size="small"
                      style={{
                        backgroundColor: t.company.status ? "#4caf50" : "#ffffff",
                        color: "#fff",
                      }}
                    />
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={t.status}
                    size="small"
                    style={{
                      backgroundColor: statusColors[t.status] || "#9e9e9e",
                      color: "#fff",
                    }}
                  />
                </TableCell>
                <TableCell align="center">{formatDate(t.createdAt)}</TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleViewDetail(t)}>
                    <VisibilityIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {loadingMore && (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {loading && !loadingMore && (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        )}
      </Paper>

      {/* Dialog de detalhes usando ChatiaDialog */}
      <ChatiaDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        title={`Transação #${selectedTransaction?.id || ""}`}
        maxWidth="md"
        hideFooter
      >
        {selectedTransaction && (
          <>
            <div className={classes.detailRow}>
              <span className={classes.detailLabel}>Plataforma:</span>
              <span>{selectedTransaction.platform?.toUpperCase()}</span>
            </div>
            <div className={classes.detailRow}>
              <span className={classes.detailLabel}>Evento:</span>
              <Chip
                label={eventLabels[selectedTransaction.event] || selectedTransaction.event}
                size="small"
                style={{
                  backgroundColor: eventColors[selectedTransaction.event] || "#9e9e9e",
                  color: "#fff",
                }}
              />
            </div>
            <div className={classes.detailRow}>
              <span className={classes.detailLabel}>Código Transação:</span>
              <span>{selectedTransaction.transactionCode || "-"}</span>
            </div>
            <div className={classes.detailRow}>
              <span className={classes.detailLabel}>Cliente:</span>
              <span>{selectedTransaction.buyerName} ({selectedTransaction.buyerEmail})</span>
            </div>
            <div className={classes.detailRow}>
              <span className={classes.detailLabel}>Telefone:</span>
              <span>{selectedTransaction.buyerPhone || "-"}</span>
            </div>
            <div className={classes.detailRow}>
              <span className={classes.detailLabel}>Produto:</span>
              <span>{selectedTransaction.productName} (ID: {selectedTransaction.productId})</span>
            </div>
            <div className={classes.detailRow}>
              <span className={classes.detailLabel}>Valor:</span>
              <span>{formatCurrency(selectedTransaction.amount, selectedTransaction.currency)}</span>
            </div>
            <div className={classes.detailRow}>
              <span className={classes.detailLabel}>Afiliado:</span>
              <span>{selectedTransaction.affiliateName || "-"} ({selectedTransaction.affiliateCode || "-"})</span>
            </div>
            <div className={classes.detailRow}>
              <span className={classes.detailLabel}>Empresa:</span>
              <span>
                {selectedTransaction.company
                  ? `#${selectedTransaction.company.id} ${selectedTransaction.company.name}`
                  : selectedTransaction.companyId || "-"}
              </span>
            </div>
            <div className={classes.detailRow}>
              <span className={classes.detailLabel}>Status:</span>
              <Chip
                label={selectedTransaction.status}
                size="small"
                style={{
                  backgroundColor: statusColors[selectedTransaction.status] || "#9e9e9e",
                  color: "#fff",
                }}
              />
            </div>
            <div className={classes.detailRow}>
              <span className={classes.detailLabel}>Detalhe:</span>
              <span>{selectedTransaction.statusDetail || "-"}</span>
            </div>
            <div className={classes.detailRow}>
              <span className={classes.detailLabel}>Data Processamento:</span>
              <span>{formatDate(selectedTransaction.processedAt)}</span>
            </div>
            {selectedTransaction.rawPayload && (
              <Box mt={2}>
                <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                  Payload Raw (JSON):
                </Typography>
                <Paper variant="outlined" style={{ padding: 12, maxHeight: 300, overflow: "auto", borderRadius: 12 }}>
                  <pre style={{ margin: 0, fontSize: 12, whiteSpace: "pre-wrap" }}>
                    {JSON.stringify(selectedTransaction.rawPayload, null, 2)}
                  </pre>
                </Paper>
              </Box>
            )}
          </>
        )}
      </ChatiaDialog>
    </MainContainer>
  );
};

export default Subscribers;
